'use client';

import { c as _c } from "react/compiler-runtime";
import AgeVerificationModal from '@/app/[locale]/(worlds)/[roomId]/components/AgeVerificationModal';
import EmbeddedAdultOverlay from '@/app/[locale]/(worlds)/[roomId]/components/EmbeddedAdultOverlay';
import VramAlertModal from '@/app/[locale]/(worlds)/[roomId]/components/VramAlertModal';
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe';
import { useAuthContext } from '@/context/AuthContext';
import { AGE_GATE_STATUS, useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useCrossPlatformGatingContext } from '@/context/CrossPlatformGatingContext';
import { useUAContext } from '@/context/UAContext';
import { buildDobOAuthUrl } from '@/lib/url';
import { AppConfig, DeviceToVram, DeviceType } from '@/types/configs';
import { useLocale } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';

// Trailer alert priority:
// premium(voucher room) -> non-preferred device -> device compatibility
//
// Blocking dialog priority:
// age verification -> VRAM performance warning
//
// VRAM warning applies to v3 PlayCanvas worlds only.
// Device compatibility gating applies to standalone content only.
// iPadOS browsers reported as desktop keep the desktop iframe flow and show
// compatibility guidance over the iframe after client-side classification.
// Full3d launches (Play CTA) skip the compatibility trailer and enter the world directly.

function isVramOverLimit(appConfig: AppConfig, deviceType: DeviceType, roomVramKb: number | undefined, vramLimitKb: number | undefined, vramLimitPassed: boolean) {
  if (!roomVramKb || !vramLimitKb || vramLimitPassed) return false;
  if (deviceType === 'desktop') {
    const alertVramKb = appConfig?.features?.device_to_vram?.find((d: DeviceToVram) => d.device === 'desktop')?.alert_vram_kb ?? -1;
    return roomVramKb > alertVramKb;
  } else {
    return roomVramKb > vramLimitKb;
  }
}
type CheckEnterRoomScreenProps = {
  children: ReactNode;
};
function CheckEnterRoomScreen(t0) {
  const $ = _c(46);
  const {
    children
  } = t0;
  const {
    roomData,
    isPreferredDevice,
    setIsTrailer,
    ageGateStatus,
    isVoucherCheckPassed,
    isPremiumRoom,
    isEmbeddedAdultBlocked
  } = useCheckEnterRoomContext();
  const {
    deviceType,
    totalVramKb: devicesVramLimit
  } = useUAContext();
  const {
    store,
    appConfig
  } = useConfigsContext();
  const {
    handleLogin,
    isSuspended
  } = useAuthContext();
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.VRAM_LIMIT_PASSED);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vramLimitPassed = t1;
  const {
    shouldShowCompatibilityTrailer
  } = useCrossPlatformGatingContext();
  const isNonPreferredDevice = !isPreferredDevice;
  let t2;
  if ($[2] !== appConfig || $[3] !== deviceType || $[4] !== devicesVramLimit || $[5] !== roomData?.total_vram_kb || $[6] !== vramLimitPassed) {
    t2 = () => isVramOverLimit(appConfig, deviceType, roomData?.total_vram_kb, devicesVramLimit, vramLimitPassed);
    $[2] = appConfig;
    $[3] = deviceType;
    $[4] = devicesVramLimit;
    $[5] = roomData?.total_vram_kb;
    $[6] = vramLimitPassed;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const [showVramAlert, setShowVramAlert] = useState(t2);
  const needsAgeGate = ageGateStatus !== AGE_GATE_STATUS.NONE;
  let t3;
  if ($[8] !== router) {
    t3 = () => {
      router.push("/");
    };
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const leaveWorld = t3;
  let t4;
  if ($[10] !== handleLogin) {
    t4 = () => {
      handleLogin({
        callback: window.location.href
      });
    };
    $[10] = handleLogin;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleSignIn = t4;
  let t5;
  let t6;
  if ($[12] !== ageGateStatus || $[13] !== isEmbeddedAdultBlocked || $[14] !== isSuspended || $[15] !== locale || $[16] !== store) {
    t5 = () => {
      if (!store) {
        return;
      }
      if (isEmbeddedAdultBlocked) {
        return;
      }
      if (isSuspended) {
        return;
      }
      if (ageGateStatus !== AGE_GATE_STATUS.NO_BIRTHDAY) {
        return;
      }
      const dobOAuthUrl = buildDobOAuthUrl({
        locale
      });
      window.location.href = dobOAuthUrl;
    };
    t6 = [ageGateStatus, locale, store, isEmbeddedAdultBlocked, isSuspended];
    $[12] = ageGateStatus;
    $[13] = isEmbeddedAdultBlocked;
    $[14] = isSuspended;
    $[15] = locale;
    $[16] = store;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  if ($[19] !== isEmbeddedAdultBlocked || $[20] !== isNonPreferredDevice || $[21] !== isVoucherCheckPassed || $[22] !== needsAgeGate || $[23] !== setIsTrailer || $[24] !== shouldShowCompatibilityTrailer || $[25] !== store) {
    t7 = () => {
      if (!store) {
        return;
      }
      if (isEmbeddedAdultBlocked) {
        return;
      }
      if (isVoucherCheckPassed === false || isNonPreferredDevice && !needsAgeGate || shouldShowCompatibilityTrailer) {
        setIsTrailer(true);
        setShowVramAlert(false);
      } else {
        if (isVoucherCheckPassed) {
          setIsTrailer(false);
        }
      }
    };
    $[19] = isEmbeddedAdultBlocked;
    $[20] = isNonPreferredDevice;
    $[21] = isVoucherCheckPassed;
    $[22] = needsAgeGate;
    $[23] = setIsTrailer;
    $[24] = shouldShowCompatibilityTrailer;
    $[25] = store;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== isEmbeddedAdultBlocked || $[28] !== isNonPreferredDevice || $[29] !== isPremiumRoom || $[30] !== isVoucherCheckPassed || $[31] !== needsAgeGate || $[32] !== setIsTrailer || $[33] !== shouldShowCompatibilityTrailer || $[34] !== store) {
    t8 = [store, isNonPreferredDevice, setIsTrailer, isPremiumRoom, isVoucherCheckPassed, shouldShowCompatibilityTrailer, needsAgeGate, isEmbeddedAdultBlocked];
    $[27] = isEmbeddedAdultBlocked;
    $[28] = isNonPreferredDevice;
    $[29] = isPremiumRoom;
    $[30] = isVoucherCheckPassed;
    $[31] = needsAgeGate;
    $[32] = setIsTrailer;
    $[33] = shouldShowCompatibilityTrailer;
    $[34] = store;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  useEffect(t7, t8);
  if (!store || isVoucherCheckPassed === undefined) {
    return null;
  }
  if (isSuspended) {
    return null;
  }
  if (isEmbeddedAdultBlocked) {
    let t9;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <EmbeddedAdultOverlay />;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    return t9;
  }
  if (ageGateStatus === AGE_GATE_STATUS.NOT_SIGNED_IN || ageGateStatus === AGE_GATE_STATUS.UNDERAGE) {
    const t9 = ageGateStatus === AGE_GATE_STATUS.NOT_SIGNED_IN ? handleSignIn : undefined;
    let t10;
    if ($[37] !== ageGateStatus || $[38] !== leaveWorld || $[39] !== t9) {
      t10 = <AgeVerificationModal status={ageGateStatus} onLeave={leaveWorld} onSignIn={t9} />;
      $[37] = ageGateStatus;
      $[38] = leaveWorld;
      $[39] = t9;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    return t10;
  } else {
    if (ageGateStatus === AGE_GATE_STATUS.NO_BIRTHDAY) {
      return null;
    } else {
      if (showVramAlert) {
        let t9;
        if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = () => setShowVramAlert(false);
          $[41] = t9;
        } else {
          t9 = $[41];
        }
        let t10;
        if ($[42] !== setIsTrailer) {
          t10 = <VramAlertModal onCancel={t9} onAction={() => {
            setShowVramAlert(false);
            setIsTrailer(true);
          }} />;
          $[42] = setIsTrailer;
          $[43] = t10;
        } else {
          t10 = $[43];
        }
        return t10;
      }
    }
  }
  let t9;
  if ($[44] !== children) {
    t9 = <>{children}</>;
    $[44] = children;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
}
export default CheckEnterRoomScreen;